import { useState, useMemo } from 'react'
import type { User, Organisation } from '@esf/shared'

// Simple icon renderer
const renderIcon = (path: string, className = 'w-5 h-5') => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
)

interface UsersPageProps {
  users: User[]
  setSelectedUser: (user: User | null) => void
  setUserModalOpen: (v: boolean) => void
  handleToggleUserEnabled: (user: User) => void
  handlePasswordReset: (user: User) => void
  isSubmitting: boolean
  setUserToDelete: (user: User) => void
  setDeleteUserModalOpen: (v: boolean) => void
  // New props for multi-tenancy
  isSuperAdmin?: boolean
  organisations?: Organisation[]
  selectedOrgFilter?: string | null
  onOrgFilterChange?: (orgId: string | null) => void
  // Soft delete props
  showDeleted: boolean
  setShowDeleted: (v: boolean) => void
  onRestoreUser: (user: User) => void
}

export default function UsersPage({
  users,
  setSelectedUser,
  setUserModalOpen,
  handleToggleUserEnabled,
  handlePasswordReset,
  isSubmitting,
  setUserToDelete,
  setDeleteUserModalOpen,
  isSuperAdmin = false,
  organisations = [],
  selectedOrgFilter = null,
  onOrgFilterChange,
  showDeleted,
  setShowDeleted,
  onRestoreUser,
}: UsersPageProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('')

  const roleColors: Record<string, string> = {
    SuperAdmin: 'bg-esf-coral/10 text-esf-coral',
    OrgAdmin: 'bg-purple-100 text-purple-700',
    AssetManager: 'bg-blue-100 text-blue-700',
    FieldTech: 'bg-green-100 text-green-700',
    Analyst: 'bg-yellow-100 text-yellow-700',
    PublicViewer: 'bg-gray-100 text-gray-700',
  }

  // Build org lookup map for display
  const orgMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const org of organisations) {
      map[org.id] = org.name
    }
    return map
  }, [organisations])

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const matchesSearch = searchTerm === '' ||
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesRole = roleFilter === '' || u.role === roleFilter
      const matchesOrg = !selectedOrgFilter || u.organisationId === selectedOrgFilter
      return matchesSearch && matchesRole && matchesOrg
    })
  }, [users, searchTerm, roleFilter, selectedOrgFilter])

  return (
    <div className="container-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isSuperAdmin ? 'Manage users across all organisations' : 'Manage your organisation\'s team members'}
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            setSelectedUser(null)
            setUserModalOpen(true)
          }}
        >
          {renderIcon('M12 4v16m8-8H4')}
          Add User
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search by name or email..."
              className="input w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="input"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">All Roles</option>
            {isSuperAdmin && <option value="SuperAdmin">SuperAdmin</option>}
            <option value="OrgAdmin">OrgAdmin</option>
            <option value="AssetManager">AssetManager</option>
            <option value="FieldTech">FieldTech</option>
            <option value="Analyst">Analyst</option>
            <option value="PublicViewer">PublicViewer</option>
          </select>
          {isSuperAdmin && organisations.length > 0 && (
            <select
              className="input"
              value={selectedOrgFilter || ''}
              onChange={(e) => onOrgFilterChange?.(e.target.value || null)}
            >
              <option value="">All Organisations</option>
              {organisations.filter(o => o.enabled !== false).map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show deleted
          </label>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="kpi">
          <div className="kpi-icon bg-purple-100 text-purple-600">
            {renderIcon('M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z')}
          </div>
          <div className="kpi-title">Total Users</div>
          <div className="kpi-value">{filteredUsers.length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon bg-success-100 text-success-600">
            {renderIcon('M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z')}
          </div>
          <div className="kpi-title">Active Users</div>
          <div className="kpi-value">{filteredUsers.filter(u => u.enabled).length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon bg-blue-100 text-blue-600">
            {renderIcon('M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z')}
          </div>
          <div className="kpi-title">Admins</div>
          <div className="kpi-value">{filteredUsers.filter(u => u.role === 'OrgAdmin' || u.role === 'SuperAdmin').length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon bg-gray-100 text-gray-600">
            {renderIcon('M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636')}
          </div>
          <div className="kpi-title">Disabled</div>
          <div className="kpi-value">{filteredUsers.filter(u => !u.enabled).length}</div>
        </div>
      </div>

      {/* Users Table */}
      <div className="table-card">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              {isSuperAdmin && <th>Organisation</th>}
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(u => (
              <tr key={u.email} className={u.deletedAt ? 'opacity-50 bg-gray-50' : ''}>
                <td className="font-medium">{u.name || '-'}</td>
                <td className="text-sm text-gray-600">{u.email}</td>
                {isSuperAdmin && (
                  <td className="text-sm text-gray-600">
                    {u.organisationId ? (orgMap[u.organisationId] || u.organisationId) : '-'}
                  </td>
                )}
                <td>
                  <span className={`badge ${roleColors[u.role] || 'bg-gray-100 text-gray-700'}`}>
                    {u.role}
                  </span>
                </td>
                <td>
                  {u.deletedAt ? (
                    <span className="badge bg-red-100 text-red-700">Deleted</span>
                  ) : (
                    <span className={`badge ${u.enabled ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'}`}>
                      {u.enabled ? 'Active' : 'Disabled'}
                    </span>
                  )}
                </td>
                <td className="text-sm text-gray-500">
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : 'Never'}
                </td>
                <td>
                  <div className="flex gap-1">
                    {u.deletedAt ? (
                      <button
                        className="btn btn-sm bg-success-50 text-success-600 hover:bg-success-100"
                        onClick={() => onRestoreUser(u)}
                        disabled={isSubmitting}
                        title="Restore user"
                      >
                        Restore
                      </button>
                    ) : (
                      <>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => {
                            setSelectedUser(u)
                            setUserModalOpen(true)
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className={`btn btn-sm ${u.enabled ? 'btn-secondary' : 'bg-success-50 text-success-600 hover:bg-success-100'}`}
                          onClick={() => handleToggleUserEnabled(u)}
                          disabled={isSubmitting}
                        >
                          {u.enabled ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          className="btn btn-sm bg-amber-50 text-amber-700 hover:bg-amber-100"
                          onClick={() => handlePasswordReset(u)}
                          disabled={isSubmitting}
                          title="Reset user's password"
                        >
                          Reset PW
                        </button>
                        <button
                          className="btn btn-sm bg-error-50 text-error-600 hover:bg-error-100"
                          onClick={() => {
                            setUserToDelete(u)
                            setDeleteUserModalOpen(true)
                          }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={isSuperAdmin ? 8 : 7}>
                  <div className="empty-state">
                    <div className="empty-state-icon bg-purple-100 text-purple-600">
                      {renderIcon('M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z')}
                    </div>
                    <div className="empty-state-title">No Users Found</div>
                    <div className="empty-state-text">
                      {searchTerm || roleFilter || selectedOrgFilter
                        ? 'No users match your filters.'
                        : 'Add team members to manage your organisation\'s assets.'}
                    </div>
                    {!searchTerm && !roleFilter && !selectedOrgFilter && (
                      <button
                        className="btn btn-primary mt-4"
                        onClick={() => {
                          setSelectedUser(null)
                          setUserModalOpen(true)
                        }}
                      >
                        {renderIcon('M12 4v16m8-8H4')}
                        Add User
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
